import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

interface StatCardProps {
  title: string;
  value: string | number;
  percentChange?: number | null;
  suffix?: string;
  inverseColors?: boolean; // For metrics where increase is bad (e.g., sudden leave)
  icon?: React.ReactNode;
  className?: string;
}

export function StatCard({
  title,
  value,
  percentChange,
  suffix = "",
  inverseColors = false,
  icon,
  className,
}: StatCardProps) {
  const hasChange = percentChange !== null && percentChange !== undefined && !isNaN(percentChange);
  const isPositive = hasChange && percentChange > 0;
  const isNegative = hasChange && percentChange < 0;

  // Determine color based on direction and whether inverse
  const getChangeColor = () => {
    if (!hasChange) return "text-muted-foreground";
    if (inverseColors) {
      // For bad metrics: increase = red, decrease = green
      return isPositive ? "text-destructive" : "text-emerald-500";
    }
    // For good metrics: increase = green, decrease = red
    return isPositive ? "text-emerald-500" : "text-destructive";
  };

  const getChangeBg = () => {
    if (!hasChange) return "bg-muted";
    if (inverseColors) {
      return isPositive ? "bg-destructive/10" : "bg-emerald-500/10";
    }
    return isPositive ? "bg-emerald-500/10" : "bg-destructive/10";
  };

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-muted-foreground truncate">{title}</p>
            <p className="text-2xl font-bold mt-1 truncate">
              {typeof value === "number" ? value.toLocaleString("th-TH") : value}
              {suffix && <span className="text-base font-normal ml-1">{suffix}</span>}
            </p>
          </div>
          {icon && (
            <div className="flex-shrink-0 p-2 rounded-lg bg-primary/10 text-primary">
              {icon}
            </div>
          )}
        </div>

        {hasChange && (
          <div className={cn("inline-flex items-center gap-1 mt-3 px-2 py-1 rounded-full text-xs font-medium", getChangeBg(), getChangeColor())}>
            {isPositive ? (
              <TrendingUp className="h-3 w-3" />
            ) : isNegative ? (
              <TrendingDown className="h-3 w-3" />
            ) : (
              <Minus className="h-3 w-3" />
            )}
            <span>
              {isPositive ? "+" : ""}
              {percentChange.toFixed(1)}%
            </span>
            <span className="text-muted-foreground">vs เดือนก่อน</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
